import { useEffect, useState } from "react";
import { Clapperboard, Plus, Trash2, X, Check, Minus } from "lucide-react";
import { api } from "../lib/apiClient";
import { useApp, MONTHS } from "../lib/store";
import { PageHeader, StatusBadge, Empty } from "../components/Shared";
import { toast } from "sonner";

const MANAGERS = [{ id: "anjali", name: "Anjali" }, { id: "hani", name: "Hani" }];
const STATUSES = ["Planned", "Script Approved", "Shoot Done", "Editing", "Posted"];
const TYPES = ["Reel", "Photoshoot", "Product Shoot", "Ad Film", "Event Coverage"];
const FLAGS = [
  ["script_ready", "Script"], ["script_approved", "Approved"], ["creator_finalized", "Creator"],
  ["location_finalized", "Location"], ["resources_allocated", "Resources"], ["shoot_completed", "Shot"],
  ["editor_handover", "Handover"],
];

function Tick({ on }) {
  return on ? <Check size={15} className="text-emerald-500" /> : <Minus size={15} className="text-muted-c opacity-40" />;
}

export default function Shoots() {
  const { month, year, can, role, selfEmployeeId } = useApp();
  const isManager = role === "manager";
  const [managerId, setManagerId] = useState(isManager ? selfEmployeeId : "anjali");
  const [rows, setRows] = useState([]);
  const [brands, setBrands] = useState([]);
  const [modal, setModal] = useState(null);

  const load = () => api.shoots({ manager_id: managerId, month, year }).then(setRows).catch(() => {});
  useEffect(() => { load(); api.brands().then(setBrands).catch(() => {}); }, [managerId, month, year]);

  const setStatus = (r, s) => api.updateShoot(r.id, { status: s }).then(() => { toast.success(`Status → ${s}`); load(); });

  return (
    <div>
      <PageHeader title="Shoot, Script & Creator Board" subtitle="Plan shoots, scripts, creators & posting deadlines">
        {can.assignTasks && <button data-testid="add-shoot-button" onClick={() => setModal({ brand: brands[0]?.name || "", month, year })} className="pill px-3 py-2 text-sm font-semibold brand-bg text-white"><Plus size={15} /> New Shoot</button>}
      </PageHeader>

      <div className="card-panel p-4 mb-5 flex flex-wrap gap-3 items-center">
        {!isManager && (
          <select value={managerId} onChange={(e) => setManagerId(e.target.value)} className="input-c" style={{ maxWidth: 200 }} data-testid="shoots-manager-picker">
            {MANAGERS.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        )}
        <span className="text-xs text-muted-c ml-auto">{MONTHS.find((m) => m.v === month)?.n} {year}</span>
      </div>

      <div className="card-panel overflow-x-auto" data-testid="shoots-table">
        <table className="data-table">
          <thead><tr><th>Date</th><th>Brand</th><th>Type</th>{FLAGS.map(([k, l]) => <th key={k} className="text-center">{l}</th>)}<th>Posting Deadline</th><th>Status</th>{can.assignTasks && <th></th>}</tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} data-testid={`shoot-row-${r.id}`}>
                <td className="mono text-xs">{(r.date || "").slice(0, 10)}</td>
                <td className="font-medium">{r.brand}</td>
                <td className="text-secondary text-xs">{r.shoot_type}</td>
                {FLAGS.map(([k]) => <td key={k} className="text-center"><div className="flex justify-center"><Tick on={r[k]} /></div></td>)}
                <td className="mono text-xs">{(r.posting_deadline || "").slice(0, 10)}</td>
                <td>
                  {can.assignTasks ? (
                    <select value={r.status} onChange={(e) => setStatus(r, e.target.value)} className="input-c py-1 text-xs" style={{ width: 140 }} data-testid={`shoot-status-${r.id}`}>
                      {STATUSES.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  ) : <StatusBadge status={r.status} />}
                </td>
                {can.assignTasks && <td className="flex gap-2"><button onClick={() => setModal(r)} className="text-muted-c hover:brand-text text-xs">Edit</button><button onClick={() => { if (window.confirm("Delete shoot?")) api.deleteShoot(r.id).then(() => { toast.success("Deleted"); load(); }); }} className="text-muted-c hover:text-red-500"><Trash2 size={15} /></button></td>}
              </tr>
            ))}
          </tbody>
        </table>
        {!rows.length && <Empty text="No shoots planned for this selection" />}
      </div>

      {modal && <ShootModal row={modal} brands={brands} managerId={managerId} month={month} year={year} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
}

function ShootModal({ row, brands, managerId, month, year, onClose, onSaved }) {
  const editing = !!row.id;
  const [f, setF] = useState({
    brand: row.brand || brands[0]?.name || "", shoot_type: row.shoot_type || "Reel",
    date: (row.date || "").slice(0, 10), posting_deadline: (row.posting_deadline || "").slice(0, 10),
    status: row.status || "Planned", remarks: row.remarks || "",
    ...Object.fromEntries(FLAGS.map(([k]) => [k, !!row[k]])),
  });
  const set = (k, v) => setF({ ...f, [k]: v });
  const save = async () => {
    const body = { ...f, manager_id: managerId, month, year };
    ["date", "posting_deadline"].forEach((k) => { body[k] = body[k] ? new Date(body[k]).toISOString() : null; });
    if (editing) await api.updateShoot(row.id, body); else await api.createShoot(body);
    toast.success(editing ? "Shoot updated" : "Shoot created"); onSaved();
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }} onClick={onClose}>
      <div className="card-panel w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="shoot-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-subtle"><h3 className="font-display font-semibold">{editing ? "Edit" : "New"} Shoot</h3><button onClick={onClose}><X size={18} className="text-muted-c" /></button></div>
        <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-semibold text-muted-c uppercase tracking-wider block mb-1.5">Brand</label><select className="input-c" value={f.brand} onChange={(e) => set("brand", e.target.value)}>{brands.map((b) => <option key={b.name} value={b.name}>{b.name}</option>)}</select></div>
            <div><label className="text-xs font-semibold text-muted-c uppercase tracking-wider block mb-1.5">Shoot Type</label><select className="input-c" value={f.shoot_type} onChange={(e) => set("shoot_type", e.target.value)}>{TYPES.map((t) => <option key={t}>{t}</option>)}</select></div>
            <div><label className="text-xs font-semibold text-muted-c uppercase tracking-wider block mb-1.5">Shoot Date</label><input type="date" className="input-c" value={f.date} onChange={(e) => set("date", e.target.value)} data-testid="shoot-date" /></div>
            <div><label className="text-xs font-semibold text-muted-c uppercase tracking-wider block mb-1.5">Posting Deadline</label><input type="date" className="input-c" value={f.posting_deadline} onChange={(e) => set("posting_deadline", e.target.value)} data-testid="shoot-deadline" /></div>
          </div>
          <div><label className="text-xs font-semibold text-muted-c uppercase tracking-wider block mb-1.5">Status</label><select className="input-c" value={f.status} onChange={(e) => set("status", e.target.value)}>{STATUSES.map((s) => <option key={s}>{s}</option>)}</select></div>
          <div className="flex flex-wrap gap-2">
            {FLAGS.map(([k, l]) => (
              <button key={k} onClick={() => set(k, !f[k])} className={`pill px-3 py-1.5 text-xs font-medium border ${f[k] ? "brand-bg text-white border-transparent" : "border-subtle text-secondary"}`}>{l}</button>
            ))}
          </div>
          <div><label className="text-xs font-semibold text-muted-c uppercase tracking-wider block mb-1.5">Remarks</label><input className="input-c" value={f.remarks} onChange={(e) => set("remarks", e.target.value)} /></div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-subtle"><button onClick={onClose} className="pill px-4 py-2 text-sm border-strong">Cancel</button><button onClick={save} className="pill px-4 py-2 text-sm brand-bg text-white font-semibold" data-testid="save-shoot-button">{editing ? "Update" : "Create"}</button></div>
      </div>
    </div>
  );
}
